import React from "react";
import { CustomButton } from "./CustomButton";
import { Surface } from "./Surface";
import {
  HiOutlineCheck,
  HiOutlineExternalLink,
  HiOutlineMail,
  HiOutlineRefresh,
} from "react-icons/hi";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import { useTranslation } from "react-i18next";

interface ErrorData {
  code?: string;
  message?: string;
  status?: number;
}

interface BusinessCardErrorProps {
  error?: ErrorData | string;
  title?: string;
  description?: string;
  showTryAgain?: boolean;
  showGoBack?: boolean;
  onTryAgain?: () => void;
  onGoBack?: () => void;
  showPromotion?: boolean;
  className?: string;
}

const PRODUCT_NAME = "Super User Manager";

const BusinessCardError: React.FC<BusinessCardErrorProps> = ({
  error,
  title,
  description,
  showTryAgain = true,
  showGoBack = true,
  onTryAgain = () => window.location.reload(),
  onGoBack = () => window.history.back(),
  showPromotion = true,
  className = "",
}) => {
  const { t } = useTranslation("clientView");
  const [signatureBefore, signatureAfter] = (
    t("event.errors.poweredBy") as string
  ).split(PRODUCT_NAME);

  const getErrorDescription = () => {
    if (description) return description;

    if (typeof error === "object" && error) {
      const { code, status, message } = error;

      switch (code) {
        case "RSUSKO026":
          return t("error.businessCard.errors.userNotFound");

        case "RSUSKO035":
          return t("error.businessCard.errors.noPermission");

        case "RSUSKO011":
          return t("error.businessCard.errors.profileLoadError");

        default:
          switch (status) {
            case 400:
              return t("error.businessCard.errors.invalidRequest");

            case 403:
              return t("error.businessCard.errors.forbidden");

            case 404:
              return t("error.businessCard.errors.notFound");

            case 500:
              return t("error.businessCard.errors.serverError");

            default:
              return message || t("error.businessCard.errors.default");
          }
      }
    }

    if (typeof error === "string") {
      if (error.includes("not a business card process")) {
        return t("error.businessCard.errors.notBusinessCard");
      }

      if (error.includes("Invalid") && error.includes("link")) {
        return t("error.businessCard.errors.invalidLink");
      }

      if (error.includes("not found")) {
        return t("error.businessCard.errors.removed");
      }

      if (error.length > 10) {
        return error;
      }
    }

    return t("error.businessCard.errors.unavailable");
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4 ${className}`}
    >
      <div className="max-w-lg w-full">
        <Surface className="shadow-lg">
          <div className="text-center py-8">
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <HiOutlineExclamationTriangle className="w-10 h-10 text-red-500" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              {title || t("error.businessCard.notAvailable")}
            </h1>

            <p className="text-gray-600 mb-6 leading-relaxed">
              {getErrorDescription()}
            </p>

            {(showTryAgain || showGoBack) && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                {showTryAgain && (
                  <CustomButton
                    variant="primary"
                    onClick={onTryAgain}
                    icon={<HiOutlineRefresh size={16} />}
                    title={t("error.businessCard.tryAgain")}
                  />
                )}
                {showGoBack && (
                  <CustomButton
                    variant="secondary"
                    onClick={onGoBack}
                    title={t("error.businessCard.goBack")}
                  />
                )}
              </div>
            )}

            {showPromotion && (
              <div className="border-t pt-8">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <HiOutlineMail className="w-6 h-6" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-3">
                    {t("error.businessCard.promotion.title")}
                  </h3>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    {t("error.businessCard.promotion.description")}
                  </p>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                    <div className="flex items-center text-blue-100">
                      <HiOutlineCheck className="w-4 h-4 mr-2 flex-shrink-0" />
                      {t("error.businessCard.promotion.features.qrCodes")}
                    </div>
                    <div className="flex items-center text-blue-100">
                      <HiOutlineCheck className="w-4 h-4 mr-2 flex-shrink-0" />
                      {t("error.businessCard.promotion.features.templates")}
                    </div>
                    <div className="flex items-center text-blue-100">
                      <HiOutlineCheck className="w-4 h-4 mr-2 flex-shrink-0" />
                      {t("error.businessCard.promotion.features.sharing")}
                    </div>
                    <div className="flex items-center text-blue-100">
                      <HiOutlineCheck className="w-4 h-4 mr-2 flex-shrink-0" />
                      {t("error.businessCard.promotion.features.management")}
                    </div>
                  </div>

                  <CustomButton
                    variant="secondary"
                    className="w-full font-medium"
                    onClick={() =>
                      window.open(
                        "https://dashboard.super-user-manager.com/register",
                        "_blank"
                      )
                    }
                    comp={
                      <span className="flex items-center gap-2">
                        {t("error.businessCard.promotion.cta")}
                        <HiOutlineExternalLink className="w-4 h-4" />
                      </span>
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </Surface>

        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            {signatureBefore}
            <button
              onClick={() =>
                window.open("https://super-user-manager.com", "_blank")
              }
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              {PRODUCT_NAME}
            </button>
            {signatureAfter}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardError;
